// src/components/enduser/auth/AuthPage.tsx - עם תמיכה בתנאי שימוש (נוסף נובמבר 2025)

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { enduserAuthService } from '../../../services/enduser/enduserAuthService';
import { subscriptionService } from '../../../services/enduser/subscriptionService';
import { mountGoogleTranslate } from '../../../utils/googleTranslate';

type AuthMode = 'login' | 'register' | 'verify';
type UserType = 'personal' | 'commercial';

interface FormData {
  name: string;
  email: string;
  phone_number: string;
  user_type: UserType;
  company_name: string;
}

const AuthPage: React.FC = () => {
  console.log('🎯 [AuthPage] Component loading!')
  const [mode, setMode] = useState<AuthMode>('login');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone_number: '',
    user_type: 'personal',
    company_name: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  
  // ✅ State חדש - תנאי שימוש (נוסף נובמבר 2025)
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  
  const navigate = useNavigate();


  // בורר השפות. הקונפיגורציה אחידה לכל האתר — ראה ההסבר ב-utils/googleTranslate.
  useEffect(
    () =>
      mountGoogleTranslate("google_translate_element_auth"),
    []
  );

  // Custom styles for phone input
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      /* Custom styling for phone input */
      .phone-input-container .react-tel-input {
        direction: ltr !important;
        text-align: left !important;
      }
      
      .phone-input-container .react-tel-input .form-control {
        width: 100% !important;
        height: 48px !important;
        padding: 12px 58px 12px 12px !important;
        border: 1px solid #d1d5db !important;
        border-radius: 6px !important;
        font-size: 16px !important;
        background: white !important;
        color: #374151 !important;
        direction: ltr !important;
        text-align: left !important;
      }
      
      .phone-input-container .react-tel-input .form-control:focus {
        border-color: #3b82f6 !important;
        outline: none !important;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
      }
      
      .phone-input-container .react-tel-input .flag-dropdown {
        border: 1px solid #d1d5db !important;
        border-radius: 6px 0 0 6px !important;
        background: white !important;
      }
      
      .phone-input-container .react-tel-input .flag-dropdown:hover {
        background: #f9fafb !important;
      }
      
      .phone-input-container .react-tel-input .flag-dropdown.open {
        background: #f3f4f6 !important;
      }
      
      .phone-input-container .react-tel-input .selected-flag {
        padding: 0 8px !important;
        width: 52px !important;
      }
      
      .phone-input-container .react-tel-input .country-list {
        direction: ltr !important;
        text-align: left !important;
        z-index: 1000 !important;
        max-height: 200px !important;
        overflow-y: auto !important;
      }
      
      .phone-input-container .react-tel-input .country-list .country {
        padding: 8px 12px !important;
        display: flex !important;
        align-items: center !important;
        gap: 8px !important;
      }
      
      .phone-input-container .react-tel-input .country-list .country:hover {
        background: #f3f4f6 !important;
      }
      
      .phone-input-container .react-tel-input .country-list .country.highlight {
        background: #eff6ff !important;
      }
      
      /* RTL adjustments for container */
      .phone-input-rtl-wrapper {
        direction: rtl;
        text-align: right;
      }
      
      .phone-input-rtl-wrapper .phone-input-container {
        direction: ltr;
        text-align: left;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handlePhoneChange = (value: string) => {
    // value הוא מה ש-react-phone-input-2 מחזיק: ספרות בלבד כולל קידומת מדינה, ללא '+'
    // שומרים '+' מוביל אחד לשליחה ל-Backend (הוולידציה ממילא מסירה תווים שאינם ספרות).
    setFormData(prev => ({
      ...prev,
      phone_number: value ? `+${value}` : ''
    }));
    setError('');
  };

  const validateForm = (): boolean => {
    if (mode === 'register') {
      if (!formData.name.trim()) {
        setError('שם מלא נדרש');
        return false;
      }
      if (!formData.phone_number.trim()) {
        setError('מספר טלפון נדרש');
        return false;
      }
      if (formData.user_type === 'commercial' && !formData.company_name.trim()) {
        setError('שם חברה נדרש עבור רישום מסחרי');
        return false;
      }
      
      // Validate phone number (must have at least 7 digits after country code)
      const phoneDigits = formData.phone_number.replace(/\D/g, '');
      if (phoneDigits.length < 7) {
        setError('מספר טלפון לא תקין');
        return false;
      }

      // ✅ וולידציה חדשה - תנאי שימוש (נוסף נובמבר 2025)
      if (!termsAccepted) {
        setError('יש לאשר את תנאי השימוש ומדיניות הפרטיות לפני ההרשמה');
        return false;
      }
    }
    if (mode === 'login') {
      if (!termsAccepted) {
        setError('יש לאשר את תנאי השימוש ומדיניות הפרטיות לפני ההתחברות');
        return false;
      }
    }
    if (!formData.email.trim()) {
      setError('כתובת אימייל נדרשת');
      return false;
    }

    if (!enduserAuthService.isValidEmail(formData.email)) {
      setError('כתובת אימייל לא תקינה');
      return false;
    }

    return true;
  };

  const handleRegister = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');
    try {
      // ✅ שליחת terms_accepted ל-Backend (נוסף נובמבר 2025)
      const result = await enduserAuthService.register({
        name: formData.name,
        email: formData.email,
        phone_number: enduserAuthService.formatInternationalPhoneNumber(formData.phone_number),
        user_type: formData.user_type,
        company_name: formData.company_name,
        terms_accepted: termsAccepted ? '1' : '0' // ✅ נתון חדש!
      });

      if (result.res === 'OK') {
        setSuccess('נשלח קוד אימות לאימייל שלך');
        setSessionToken(result.data?.session_token || '');
        setMode('verify');
      } else {
        // ✅ טיפול בשגיאה חדשה (נוסף נובמבר 2025)
        if (result.error_code === 'TERMS_NOT_ACCEPTED') {
          setError('חובה לאשר את תנאי השימוש ומדיניות הפרטיות');
        } else {
          setError(result.msg || 'שגיאה ברישום');
        }
      }
    } catch (error) {
      setError('שגיאה בהתחברות לשרת');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    try {
      const result = await enduserAuthService.login({
     email: formData.email,
     terms_accepted: termsAccepted ? '1' : '0' // ✅ נתון חדש!
   });;

      if (result.res === 'OK') {
        setSuccess('נשלח קוד התחברות לאימייל שלך');
        setSessionToken(result.data?.session_token || '');
        setMode('verify');
      } else {
        // ✅ טיפול בשגיאה חדשה - משתמש ישן ללא תנאים (נוסף נובמבר 2025)
        if (result.error_code === 'TERMS_NOT_ACCEPTED_OLD_USER') {
          setError('יש לאשר את תנאי השימוש ומדיניות הפרטיות. אנא צור קשר עם התמיכה.');
        } else {
          setError(result.msg || 'שגיאה בהתחברות');
        }
      }
    } catch (error) {
      setError('שגיאה בהתחברות לשרת');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode.trim()) {
      setError('יש להזין קוד אימות');
      return;
    }

    if (!/^\d{6}$/.test(verificationCode)) {
      setError('קוד אימות חייב להכיל 6 ספרות');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      console.log('🔍 Starting email verification...');
      
      const result = await enduserAuthService.verifyEmail({
        session_token: sessionToken || enduserAuthService.getSessionToken() || '',
        verification_code: verificationCode
      });

      // console.log('🔍 Verification result:', result);

      if (result.res === 'OK') {
  setSuccess('אימות הושלם בהצלחה!');
  
  setTimeout(async () => {
    try {
      // בדיקת סטטוס מנוי
      const statusResult = await subscriptionService.getSubscriptionStatus();
      
      if (statusResult.res === 'OK' && statusResult.data) {
        const status = statusResult.data.subscription_status;
        // אם יש מנוי פעיל, תקופת ניסיון, או גישה חופשית - לדשבורד
        if (status === 'active' || status === 'trial' || status === 'free_access') {
          navigate('/enduser/dashboard');
        } else {
          // אין מנוי - לדף מנויים
          navigate('/enduser/subscription');
        }
      } else {
        // שגיאה בבדיקה - לדף מנויים כברירת מחדל
        navigate('/enduser/subscription');
      }
    } catch (navError) {
      console.error('❌ Navigation error:', navError);
      navigate('/enduser/subscription');
    }
  }, 1500);
  
} else {
        // console.log('❌ Verification failed:', result.msg);
        setError(result.msg || 'קוד אימות שגוי');
      }
    } catch (error) {
      console.error('❌ Verification error:', error);
      setError('שגיאה באימות');
    } finally {
      setIsLoading(false);
    }
  };

  const renderLoginForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          כתובת אימייל
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="your@email.com"
          disabled={isLoading}
          dir="ltr"
        />
      </div>
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="terms-login"
            checked={termsAccepted}
            onChange={(e) => {
              setTermsAccepted(e.target.checked);
              setError('');
            }}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            disabled={isLoading}
          />
          <label htmlFor="terms-login" className="text-sm text-gray-700 flex-1">
            קראתי ואני מאשר/ת את{' '}
            <Link
              to="/terms-and-privacy"
              target="_blank"
              className="text-blue-600 hover:text-blue-800 underline font-semibold"
            >
              תנאי השימוש ומדיניות הפרטיות
            </Link>
            {' '}*
          </label>
        </div>
      </div>
      <button
        onClick={handleLogin}
        disabled={isLoading}
        className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {isLoading ? 'שולח קוד...' : 'שלח קוד התחברות'}
      </button>
    </div>
  );

  const renderRegisterForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          שם מלא *
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="שם פרטי ומשפחה"
          disabled={isLoading}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          כתובת אימייל *
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="your@email.com"
          disabled={isLoading}
          dir="ltr"
        />
      </div>

      <div className="phone-input-rtl-wrapper">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          מספר טלפון *
        </label>
        <p className="text-xs text-gray-500 mb-2 text-right">
          בחר את קידומת המדינה והזן את המספר <strong>ללא ה-0 המוביל</strong> (לדוגמה עבור ישראל: 541234567).
        </p>
        <div className="phone-input-container">
          <PhoneInput
            country={'il'}
            countryCodeEditable={false}
            value={formData.phone_number.replace(/\D/g, '')}
            onChange={handlePhoneChange}
            disabled={isLoading}
            inputProps={{
              name: 'phone',
              required: true,
              autoFocus: false,
              placeholder: 'Enter phone number'
            }}
            containerStyle={{
              width: '100%',
              direction: 'ltr'
            }}
            inputStyle={{
              width: '100%',
              height: '48px',
              fontSize: '16px',
              direction: 'ltr',
              textAlign: 'left'
            }}
            buttonStyle={{
              direction: 'ltr'
            }}
            dropdownStyle={{
              direction: 'ltr',
              textAlign: 'left'
            }}
            searchStyle={{
              direction: 'ltr',
              textAlign: 'left'
            }}
            enableSearch={true}
            disableSearchIcon={false}
            searchPlaceholder="חפש מדינה..."
            specialLabel=""
            priority={{
              il: 0,
              us: 1,
              gb: 2,
              fr: 3,
              de: 4,
              ru: 5
            }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          בחר מדינה והזן מספר טלפון מקומי
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          סוג רישום *
        </label>
        <select
          name="user_type"
          value={formData.user_type}
          onChange={handleInputChange}
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={isLoading}
        >
          <option value="personal">אישי</option>
          <option value="commercial">מסחרי</option>
        </select>
      </div>

      {formData.user_type === 'commercial' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            שם חברה *
          </label>
          <input
            type="text"
            name="company_name"
            value={formData.company_name}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="שם החברה"
            disabled={isLoading}
          />
        </div>
      )}

      {/* ✅ Checkbox חדש - תנאי שימוש (נוסף נובמבר 2025) */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="terms-register"
            checked={termsAccepted}
            onChange={(e) => {
              setTermsAccepted(e.target.checked);
              setError('');
            }}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            disabled={isLoading}
          />
          <label htmlFor="terms-register" className="text-sm text-gray-700 flex-1">
            קראתי ואני מאשר/ת את{' '}
            <Link
              to="/terms-and-privacy"
              target="_blank"
              className="text-blue-600 hover:text-blue-800 underline font-semibold"
            >
              תנאי השימוש ומדיניות הפרטיות
            </Link>
            {' '}*
          </label>
        </div>
      </div>

      <button
        onClick={handleRegister}
        disabled={isLoading}
        className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
      >
        {isLoading ? 'נרשם...' : 'הירשם'}
      </button>
    </div>
  );

  const renderVerifyForm = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <div className="text-4xl mb-4">📧</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          נשלח קוד אימות
        </h3>
        <p className="text-gray-600">
          הזנת קוד בן 6 ספרות שנשלח לכתובת:
        </p>
        <p className="font-medium text-blue-600 mt-1">{formData.email}</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          קוד אימות
        </label>
        <input
          type="text"
          value={verificationCode}
          onChange={(e) => {
            setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6));
            setError('');
          }}
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg tracking-wider"
          placeholder="123456"
          maxLength={6}
          disabled={isLoading}
          dir="ltr"
        />
        <p className="text-xs text-gray-500 mt-1 text-center">הזן 6 ספרות</p>
      </div>

      <button
        onClick={handleVerify}
        disabled={isLoading || verificationCode.length !== 6}
        className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {isLoading ? 'מאמת...' : 'אמת קוד'}
      </button>

      <div className="text-center">
        <button
          onClick={() => {
            setMode(sessionToken ? 'login' : 'register');
            setVerificationCode('');
            setError('');
            setSuccess('');
          }}
          className="text-sm text-gray-600 hover:text-gray-800 underline"
        >
          חזור לשלח קוד חדש
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50" dir="rtl">
      {/* Google Translate */}
      <div id="google_translate_element_auth" className="absolute top-4 right-4"></div>

      <div className="max-w-md w-full space-y-8 p-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">CarTax</h1>
            <p className="text-gray-600">מערכת חישוב מיסי יבוא לרכבים</p>
          </div>

          {/* Tabs */}
          {mode !== 'verify' && (
            <div className="flex border-b border-gray-200 mb-6">
              <button
                onClick={() => {
                  setMode('login');
                  setError('');
                  setSuccess('');
                  setTermsAccepted(false);
                }}
                className={`flex-1 py-2 px-4 text-center border-b-2 transition-colors ${
                  mode === 'login'
                    ? 'border-blue-500 text-blue-600 font-medium'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                התחברות
              </button>
              <button
                onClick={() => {
                  setMode('register');
                  setError('');
                  setSuccess('');
                  setTermsAccepted(false);
                }}
                className={`flex-1 py-2 px-4 text-center border-b-2 transition-colors ${
                  mode === 'register'
                    ? 'border-green-500 text-green-600 font-medium'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                הרשמה
              </button>
            </div>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-sm text-center">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-green-700 text-sm text-center">{success}</p>
            </div>
          )}

          {/* Forms */}
          {mode === 'login' && renderLoginForm()}
          {mode === 'register' && renderRegisterForm()}
          {mode === 'verify' && renderVerifyForm()}

          {/* Footer - עודכן עם קישור חדש (נובמבר 2025) */}
          <div className="mt-8 text-center text-xs text-gray-500">
            <p>
              על ידי המשך השימוש אתה מסכים ל
              <Link to="/terms-and-privacy" className="text-blue-600 hover:underline mx-1">
                תנאי השימוש ומדיניות הפרטיות
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;